<template>
  <div
    class="offcanvas offcanvas-fullscreen-sm-down offcanvas-end"
    tabindex="-1"
    id="OffcanvasLoyaltyProgramForm"
    ref="offcanvas"
    data-bs-backdrop="static"
    style="--bs-offcanvas-width: 720px;"
  >
    <div class="bg-body-secondary border offcanvas-header py-2">
      {{ type === 'CREATE' ? $t('Create Program') : $t('Edit Program') }}
      <button
        type="button"
        style="font-size: small;"
        class="btn-close shadow-none text-reset"
        data-bs-dismiss="offcanvas"
        aria-label="Close"
      />
    </div>

    <div
      class="offcanvas-body"
      v-if="loading"
    >
      <placeholder-content :is-load="true" />
    </div>
    <div class="offcanvas-body" v-else-if="!hasPermission">
      <access-denied/>
    </div>
    <div
      class="offcanvas-body p-0"
      v-else
    >
      <!-- Program Information -->
      <div class="border-bottom mb-3 px-3 py-2">
        <i class="fa-solid fa-circle-info me-2" />
        {{ $t('Program Information') }}
      </div>
      <div class="px-3">
        <div class="row g-3 py-2">
          <div class="col-md-8">
            <label for="name" class="form-label">
              {{ $t('Name') }} <span class="text-danger">*</span>
            </label>
            <input
              id="name"
              type="text"
              v-model.trim="v$.form.name.$model"
              class="form-control form-control-sm"
              :class="v$.form.name.$error || serverError('name') ? 'is-invalid' : ''"
              :placeholder="$t('Enter program name') + '...'"
            >
            <div v-for="error of v$.form.name.$errors" class="invalid-feedback" :key="error.$uid">
              {{ error.$message }}
            </div>
            <div v-for="error of serverErrors('name')" class="invalid-feedback" :key="error">{{ $t(error) }}</div>
          </div>
          <div class="col-md-4">
            <label for="code" class="form-label">{{ $t('Code') }}</label>
            <input
              id="code"
              type="text"
              v-model.trim="v$.form.code.$model"
              class="form-control form-control-sm"
              :class="v$.form.code.$error || serverError('code') ? 'is-invalid' : ''"
              :placeholder="$t('Enter code') + '...'"
            >
            <div v-for="error of v$.form.code.$errors" class="invalid-feedback" :key="error.$uid">
              {{ error.$message }}
            </div>
            <div v-for="error of serverErrors('code')" class="invalid-feedback" :key="error">{{ $t(error) }}</div>
          </div>
          <!--
            ซ่อน "โปรแกรมหลัก / เฉพาะโปรแกรมนี้ / ระดับความสำคัญ" — ระบบรองรับโปรแกรมเดียว
            ทั้ง 3 ช่องมีความหมายเฉพาะตอนมีหลายโปรแกรมมาชนกัน (ดู CreateMiddleware)
            API บังคับ is_base=true, exclusive=false, priority=0 ให้เองตอนบันทึก
            แคมเปญแต้มรายสินค้าให้ไปใช้ "โปรโมชั่น" แทน — มีขอบเขต/โควต้า/ช่วงเวลาครบกว่า
          -->
        </div>
      </div>

      <!-- Earn Rules -->
      <div class="border-bottom mb-3 px-3 py-2">
        <i class="fa-solid fa-coins me-2" />
        {{ $t('Earn Rules') }}
      </div>
      <div class="px-3">
        <div class="row g-3 py-2">
          <!--
            ซ่อน "วิธีคิดแต้ม / ขอบเขต" — โปรแกรมเดียวเป็นโปรแกรมหลักเสมอ และเส้นทาง
            แต้มฐานใน computeLots() ไม่อ่าน earn_basis/earn_scope เลย (2 ค่านี้ถูกอ่าน
            เฉพาะตอนตัดสินโปรแกรมพิเศษ ซึ่งสร้างไม่ได้แล้ว) เปิดให้เลือก = กับดักเงียบ:
            เลือก "ต่อชิ้น" แล้วลูกค้าไม่ได้แต้มเลยโดยไม่มี error
            API บังคับ earn_basis=by_spend, earn_scope=all_eligible ให้เองตอนบันทึก
          -->
          <div class="col-md-6">
            <label class="form-label">{{ $t('Points per Baht') }} <span class="text-danger">*</span></label>
            <input
              type="number" min="0" step="0.0001"
              v-model.number="v$.form.point_per_baht.$model"
              class="form-control form-control-sm"
              :class="v$.form.point_per_baht.$error ? 'is-invalid' : ''"
              placeholder="0.0000"
            >
            <div v-for="error of v$.form.point_per_baht.$errors"
              class="invalid-feedback d-block" :key="error.$uid">
              {{ error.$message }}
            </div>
            <small class="text-muted">{{ $t('Points earned per 1 baht spent — e.g. 0.05 means 20 baht = 1 point.') }}</small>
          </div>
          <div class="col-md-6">
            <label class="form-label">{{ $t('Earn Start') }}</label>
            <date-range-picker
              v-model="form.earnStartRange"
              :date-range="form.earnStartRange"
              :single-date-picker="true"
              :ranges="false"
              :auto-apply="true"
              :show-dropdowns="true"
              :locale-data="dateRangeLocale"
              :control-container-class="''"
            >
              <template v-slot:input="picker">
                <span v-if="picker.startDate">{{ formatDate(picker.startDate) }}</span>
                <span v-else class="text-muted">{{ $t('Select date') }}</span>
              </template>
            </date-range-picker>
          </div>
          <div class="col-md-6">
            <label class="form-label">{{ $t('Earn End') }}</label>
            <date-range-picker
              v-model="form.earnEndRange"
              :date-range="form.earnEndRange"
              :single-date-picker="true"
              :ranges="false"
              :auto-apply="true"
              :show-dropdowns="true"
              :locale-data="dateRangeLocale"
              :control-container-class="''"
            >
              <template v-slot:input="picker">
                <span v-if="picker.startDate">{{ formatDate(picker.startDate) }}</span>
                <span v-else class="text-muted">{{ $t('Select date') }}</span>
              </template>
            </date-range-picker>
          </div>
        </div>
      </div>

      <!--
        ถอดแผง "สินค้าที่ได้แต้ม" — โผล่เฉพาะตอน earn_scope=specific_products ซึ่งบังคับเป็น
        all_eligible ไปแล้ว และต่อให้ตั้งได้ loyalty_program_products ก็ไม่เคยถูกอ่าน
        ในเส้นทางแต้มฐาน (computeLots() match รายสินค้าเฉพาะกับโปรแกรมพิเศษ)
        แคมเปญแต้มรายสินค้า → ใช้ "โปรโมชั่น" แทน
        ⚠️ ตอนนี้ยัง "ยกเว้นสินค้ารายตัวไม่ให้แต้มไม่ได้" — products.is_point_eligible มีในตาราง
           และ computeLots() อ่านจริง แต่ไม่มี UI/rule ให้ตั้ง เลยเป็น true ทุกแถว
           ตั้งใจไม่ทำช่องนี้ที่หน้าสินค้า เพราะร้านที่ไม่ได้เปิด loyalty จะเห็นช่องที่ไม่มีความหมาย
      -->

      <!-- Expiry -->
      <div class="border-bottom mb-3 px-3 py-2">
        <i class="fa-solid fa-hourglass-half me-2" />
        {{ $t('Expiry') }}
      </div>
      <div class="px-3">
        <div class="row g-3 py-2">
          <div class="col-md-6">
            <label class="form-label">{{ $t('Expiry Mode') }} <span class="text-danger">*</span></label>
            <select v-model="form.expiry_mode" class="form-select form-select-sm">
              <option value="none">{{ $t('expiry_mode_none') }}</option>
              <option value="fixed_date">{{ $t('expiry_mode_fixed_date') }}</option>
              <option value="end_of_year">{{ $t('expiry_mode_end_of_year') }}</option>
              <option value="rolling">{{ $t('expiry_mode_rolling') }}</option>
            </select>
          </div>
          <div class="col-md-6" v-if="form.expiry_mode === 'fixed_date'">
            <label class="form-label">{{ $t('Expiry Date') }} <span class="text-danger">*</span></label>
            <date-range-picker
              v-model="form.expiryDateRange"
              :date-range="form.expiryDateRange"
              :single-date-picker="true"
              :ranges="false"
              :auto-apply="true"
              :show-dropdowns="true"
              :locale-data="dateRangeLocale"
              :control-container-class="''"
              @update="syncExpiryDate"
            >
              <template v-slot:input="picker">
                <span v-if="picker.startDate">{{ formatDate(picker.startDate) }}</span>
                <span v-else class="text-muted">{{ $t('Select date') }}</span>
              </template>
            </date-range-picker>
            <div v-for="error of v$.form.expiry_date.$errors" class="invalid-feedback d-block" :key="error.$uid">
              {{ error.$message }}
            </div>
          </div>
          <div class="col-md-6" v-if="form.expiry_mode === 'rolling'">
            <label class="form-label">{{ $t('Expiry Months') }} <span class="text-danger">*</span></label>
            <input type="number" min="1" step="1" v-model.number="v$.form.expiry_months.$model"
              class="form-control form-control-sm" :class="v$.form.expiry_months.$error ? 'is-invalid' : ''">
            <div v-for="error of v$.form.expiry_months.$errors" class="invalid-feedback d-block" :key="error.$uid">
              {{ error.$message }}
            </div>
          </div>
        </div>
      </div>

      <!-- Rewards -->
      <div class="border-bottom mb-3 px-3 py-2 d-flex justify-content-between align-items-center">
        <span><i class="fa-solid fa-gift me-2" />{{ $t('Rewards') }}</span>
        <button type="button" class="btn btn-sm btn-outline-primary" @click="addReward">
          <i class="fa-solid fa-circle-plus me-1" />{{ $t('Add') }}
        </button>
      </div>
      <div class="px-3 pb-3">
        <div
          v-for="(rw, idx) in form.rewards"
          :key="'rw-' + idx"
          class="border rounded p-2 mb-2"
        >
          <div class="row g-2 align-items-end">
            <div class="col-md-5">
              <label class="form-label mb-1" style="font-size: 12px;">{{ $t('Reward Name') }}</label>
              <input type="text" v-model.trim="rw.name" class="form-control form-control-sm"
                :placeholder="$t('Enter reward name') + '...'">
            </div>
            <div class="col-md-4">
              <label class="form-label mb-1" style="font-size: 12px;">{{ $t('Reward Type') }}</label>
              <select v-model="rw.reward_type" class="form-select form-select-sm">
                <option value="free_product">{{ $t('reward_type_free_product') }}</option>
                <option value="discount">{{ $t('reward_type_discount') }}</option>
              </select>
            </div>
            <div class="col-md-2">
              <label class="form-label mb-1" style="font-size: 12px;">{{ $t('Point Cost') }}</label>
              <input type="number" min="0" step="1" v-model.number="rw.point_cost" class="form-control form-control-sm">
            </div>
            <div class="col-md-1 text-end">
              <button type="button" class="btn btn-sm btn-outline-danger" @click="form.rewards.splice(idx, 1)">
                <i class="fa-solid fa-trash-can" />
              </button>
            </div>

            <template v-if="rw.reward_type === 'free_product'">
              <div class="col-md-12">
                <label class="form-label mb-1" style="font-size: 12px;">{{ $t('Free Product') }}</label>
                <select2 v-model="rw.reward_product_id" :options="productOptions" :settings="{ width: '100%', dropdownParent: '#OffcanvasLoyaltyProgramForm' }" />
              </div>
            </template>
            <template v-else>
              <div class="col-md-12">
                <label class="form-label mb-1" style="font-size: 12px;">{{ $t('_loyalty.Discount Amount') }} (฿)</label>
                <input type="number" min="0" step="0.01" v-model.number="rw.value" class="form-control form-control-sm">
              </div>
            </template>
          </div>
        </div>
        <div v-if="!form.rewards.length" class="text-muted text-center py-2" style="font-size: 13px;">
          {{ $t('No rewards yet') }}
        </div>
      </div>
    </div>

    <div
      class="bg-light border-top justify-content-center modal-footer py-2"
      v-if="!loading"
    >
      <ladda
        text="Save"
        :btn-class="'btn btn-primary btn-block '"
        :loading="loadingSave"
        icon="<i class='fa-solid fa-floppy-disk'></i>"
        :disabled="v$.$invalid"
        @click="type === 'CREATE' ? handleCreate() : handleUpdate()"
      />
      <button
        type="button"
        data-bs-dismiss="offcanvas"
        class="border-dark-subtle btn-sm btn btn-light me-2 ms-2"
      >
        {{ $t('Cancel') }}
      </button>
    </div>
  </div>
</template>

<script lang="js">
import PlaceholderContent from '@/plugins/PlaceholderContent.vue';
import AccessDenied from "@/plugins/AccessDenied.vue";
import Ladda from '@/plugins/Ladda.vue';
import { useVuelidate } from '@vuelidate/core';
import validation from "./assets/template/validation";
import { Loyalty } from '@/services/endpoint/loyalty.service';
import Select2 from 'vue3-select2-component';
import Popper from "vue3-popper";
import DateRangePicker from 'vue3-daterange-picker';

export default {
  setup: () => ({ v$: useVuelidate() }),
  validationConfig: { $lazy: true },
  validations() { return validation; },
  props: {
    type: { type: String, default: 'CREATE' },
    programId: { type: Number, default: 0 }
  },
  data() {
    return {
      loading: true,
      hasPermission: false,
      loadingSave: false,
      productOptions: [],
      errors: {},
      oldParams: {},
      form: {
        name: '',
        code: '',
        // ระบบรองรับโปรแกรมเดียว → เป็นโปรแกรมหลักเสมอ (API บังคับซ้ำอีกชั้น)
        is_base: true,
        exclusive: false,
        priority: 0,
        // บังคับ by_spend/all_eligible เสมอ — เส้นทางแต้มฐานไม่อ่าน 2 ค่านี้ (API บังคับซ้ำอีกชั้น)
        earn_basis: 'by_spend',
        earn_scope: 'all_eligible',
        point_per_baht: '',
        earnStartRange: { startDate: null, endDate: null },
        earnEndRange: { startDate: null, endDate: null },
        expiry_mode: 'none',
        expiry_date: '',
        expiryDateRange: { startDate: null, endDate: null },
        expiry_months: '',
        rewards: []
      }
    };
  },
  components: { PlaceholderContent, AccessDenied, Ladda, Select2, Popper, DateRangePicker },
  watch: {
    // sync expiry_date จาก v-model ของ date-picker (เชื่อถือได้กว่า @update ที่ single-mode ไม่ค่อย fire)
    'form.expiryDateRange': {
      handler(val) {
        this.form.expiry_date = (val && val.startDate) ? this.formatDate(val.startDate) : '';
        this.v$.form.expiry_date.$touch();
      },
      deep: true
    }
  },
  computed: {
    dateRangeLocale() {
      return {
        firstDay: 1,
        format: 'yyyy-mm-dd',
        applyLabel: this.$t('Apply'),
        cancelLabel: this.$t('Cancel'),
        clearable: true,
        monthNames: this.$storeIndex.monthNames(),
        daysOfWeek: this.$storeIndex.daysOfWeek()
      };
    }
  },
  methods: {
    syncExpiryDate(val) {
      this.form.expiry_date = (val && val.startDate) ? this.formatDate(val.startDate) : '';
      this.v$.form.expiry_date.$touch();
    },
    formatDate(d) {
      if (!d) return '';
      const dt = new Date(d);
      if (isNaN(dt.getTime())) return String(d).slice(0, 10);
      const m = String(dt.getMonth() + 1).padStart(2, '0');
      const day = String(dt.getDate()).padStart(2, '0');
      return `${dt.getFullYear()}-${m}-${day}`;
    },
    serverError(field) {
      return this.errors?.[field]?.length && this.oldParams[field] == this.form[field];
    },
    serverErrors(field) {
      return this.oldParams[field] == this.form[field] ? (this.errors?.[field] || []) : [];
    },
    addReward() {
      this.form.rewards.push({
        name: '', reward_type: 'free_product', reward_product_id: '',
        value: '', point_cost: 0
      });
    },
    setProductOptions(products) {
      this.productOptions = (products || []).map(p => ({ id: p.id, text: p.name }));
    },
    async getCreateData() {
      await Loyalty.getProgramCreate().then(res => {
        const data = res.data?.data ?? res.data ?? {};
        this.setProductOptions(data.products);
        this.hasPermission = true;
      }).catch(err => {
        if (err?.response?.status === 403) {
          this.hasPermission = false;
          this.loading = false;
        }
        throw err;
      });
    },
    async getUpdateData() {
      await Loyalty.getProgramUpdate(this.programId).then(res => {
        const payload = res.data?.data;
        const data = payload?.data ?? payload;
        const filter = res.data?.filter ?? payload?.filter ?? {};
        this.setProductOptions(filter.products || data.products || []);

        this.form.name = data.name || '';
        this.form.code = data.code || '';
        this.form.is_base = !!data.is_base;
        this.form.exclusive = !!data.exclusive;
        this.form.priority = parseInt(data.priority) || 0;
        this.form.point_per_baht = data.point_per_baht ?? '';
        const es = data.earn_start ? String(data.earn_start).slice(0, 10) : null;
        const ee = data.earn_end ? String(data.earn_end).slice(0, 10) : null;
        this.form.earnStartRange = { startDate: es, endDate: es };
        this.form.earnEndRange = { startDate: ee, endDate: ee };
        this.form.expiry_mode = data.expiry_mode || 'none';
        this.form.expiry_date = data.expiry_date ? String(data.expiry_date).slice(0, 10) : '';
        this.form.expiryDateRange = { startDate: this.form.expiry_date || null, endDate: this.form.expiry_date || null };
        this.form.expiry_months = data.expiry_months ?? '';
        this.form.rewards = (data.rewards || []).map(r => ({
          id: r.id,
          name: r.name || '',
          reward_type: r.reward_type || 'free_product',
          reward_product_id: r.reward_product_id ?? '',
          value: r.value ?? '',
          point_cost: parseInt(r.point_cost) || 0
        }));
        this.hasPermission = true;
      }).catch(err => {
        if (err?.response?.status === 403) {
          this.hasPermission = false;
          this.loading = false;
        }
        throw err;
      });
    },
    // datetime-local needs "YYYY-MM-DDTHH:mm"
    toLocalInput(value) {
      if (!value) return '';
      return String(value).replace(' ', 'T').slice(0, 16);
    },
    setParamsBeforeSend() {
      const f = _.cloneDeep(this.form);
      const params = {
        name: f.name,
        code: f.code || undefined,
        is_base: f.is_base ? 1 : 0,
        exclusive: f.exclusive ? 1 : 0,
        priority: parseInt(f.priority) || 0,
        earn_basis: f.earn_basis,
        earn_scope: f.earn_scope,
        point_per_baht: f.point_per_baht,
        expiry_mode: f.expiry_mode,
      };
      // earn window (optional + อิสระ): single-date picker → start ต้นวัน, end ท้ายวัน
      if (f.earnStartRange && f.earnStartRange.startDate) params.earn_start = this.formatDate(f.earnStartRange.startDate) + ' 00:00:00';
      if (f.earnEndRange && f.earnEndRange.startDate) params.earn_end = this.formatDate(f.earnEndRange.startDate) + ' 23:59:59';
      // Expiry params depend on mode.
      if (f.expiry_mode === 'fixed_date') params.expiry_date = f.expiry_date;
      if (f.expiry_mode === 'rolling') params.expiry_months = parseInt(f.expiry_months) || 0;
      // Rewards — trim type-irrelevant fields.
      params.rewards = f.rewards.filter(r => r.name).map(r => {
        const base = {
          id: r.id,
          name: r.name,
          reward_type: r.reward_type,
          point_cost: parseInt(r.point_cost) || 0
        };
        if (r.reward_type === 'free_product') {
          base.reward_product_id = r.reward_product_id;
        } else {
          base.value = r.value; // discount = ยอดบาท (ไม่มี discount_type แล้ว)
        }
        return base;
      });
      return params;
    },
    async handleCreate() {
      this.loadingSave = true;
      this.v$.$touch();
      if (this.v$.$invalid) { this.loadingSave = false; return; }
      try {
        await Loyalty.createProgram(this.setParamsBeforeSend());
        this.$parent.fetchData();
        this.$toast.success("<B>" + this.$t('Success') + "</B>\n" + this.$t('Save Program Success!'));
        $(this.$refs.offcanvas).offcanvas('hide');
      } catch (err) {
        if (err?.response?.data?.errors) {
          this.oldParams = _.clone(this.form);
          this.errors = err.response.data.errors;
        } else {
          this.$toast.warn(this.$t(err?.response?.data?.message));
        }
      } finally {
        this.loadingSave = false;
      }
    },
    async handleUpdate() {
      this.loadingSave = true;
      this.v$.$touch();
      if (this.v$.$invalid) { this.loadingSave = false; return; }
      try {
        await Loyalty.updateProgram(this.programId, this.setParamsBeforeSend());
        this.$parent.fetchData();
        this.$toast.success("<B>" + this.$t('Success') + "</B>\n" + this.$t('Update Program Success!'));
        $(this.$refs.offcanvas).offcanvas('hide');
      } catch (err) {
        if (err?.response?.data?.errors) {
          this.oldParams = _.clone(this.form);
          this.errors = err.response.data.errors;
        } else {
          this.$toast.warn(this.$t(err?.response?.data?.message));
        }
      } finally {
        this.loadingSave = false;
      }
    },
  },
  async mounted() {
    $(this.$refs.offcanvas).offcanvas('show');
    $(this.$refs.offcanvas).on('shown.bs.offcanvas', async () => {
      this.loading = true;
      try {
        if (this.type === 'CREATE') await this.getCreateData();
        if (this.type === 'EDIT') await this.getUpdateData();
        this.loading = false;
      } catch (err) {
        console.error(err);
        this.loading = false;
      }
    });
    $(this.$refs.offcanvas).on('hidden.bs.offcanvas', () => {
      this.$parent.offcanvasFroms.open = false;
    });
  }
};
</script>
